import { computed, ref, ComputedRef, Ref, onBeforeUnmount } from 'vue';
import { ColumnOrderState, ColumnPinningState } from '@tanstack/vue-table';
import { debounce, isEqual, merge } from 'lodash-es';
import { useEnvStore } from '../core/useEnvStore';
import type { Emits, Props } from '../../modernGrid.setup';
import type {
  GridColumnDef,
  GridRawRowDataType,
  GridTableInstance,
  GridTableInstanceOptions,
  GridRowSelectionOption,
  GridDetailRowOption,
  GridColumnOrderingOption,
  GridColumnPinningOption,
} from '../../modernGrid.types';
import {
  CHECKBOX_COLUMN_ID,
  EXPAND_BTN_COLUMN_ID,
  NUMBER_COLUMN_ID,
  defaultColumnOrderingOption,
  defaultColumnPinningOption,
} from '../../modernGrid.define';
import { getIsDefaultColumn, isChangedEnv } from '../../modernGrid.utils';

export const useColumnOrderAndPinningData = <RowDataType extends GridRawRowDataType>({
  props,
  emit,
  table,
  expandingColumn: _expandingColumn,
  useNumberColumn,
  rowSelectionInfo,
  detailRowInfo,
  envKey,
}: {
  props: Props<RowDataType>;
  emit: Emits<RowDataType>;
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  expandingColumn: Ref<string>;
  useNumberColumn: ComputedRef<boolean>;
  rowSelectionInfo: ComputedRef<GridRowSelectionOption>;
  detailRowInfo: ComputedRef<GridDetailRowOption>;
  envKey: Ref<string | undefined>;
}) => {
  const { getEnv, saveEnv } = useEnvStore(props.useDashboardStore ?? false);
  const columnOrderingInfo = computed<GridColumnOrderingOption>(() =>
    merge({}, defaultColumnOrderingOption, props.option?.columnOrdering),
  );
  const columnPinningInfo = computed<GridColumnPinningOption>(() =>
    merge({}, defaultColumnPinningOption, props.option?.columnPinning),
  );
  const leftColumnIds = computed<string[]>(
    () =>
      table.value
        ?.getLeftLeafColumns()
        ?.filter((col) => col.getIsVisible())
        ?.map(({ id }) => id) || [],
  );
  const rightColumnIds = computed<string[]>(
    () =>
      table.value
        ?.getRightLeafColumns()
        ?.filter((col) => col.getIsVisible())
        ?.map(({ id }) => id) || [],
  );
  const columnPinningState = ref<ColumnPinningState>({ left: [], right: [] });
  const columnOrderState = ref<ColumnOrderState>([]);

  const saveEnvColumnOrder = debounce(async (columnOrder: ColumnOrderState) => {
    const envData = getEnv(envKey.value);
    if (isChangedEnv(envData?.columnOrder, columnOrder, true)) {
      await saveEnv(envKey.value, { ...envData, columnOrder });
    }
  }, 300);

  const emitColumnOrder = debounce((order: ColumnOrderState) => {
    const filteredOrder = order.filter((columnId) => !getIsDefaultColumn(columnId));
    emit('change-column-order', filteredOrder);
  }, 100);

  const emitColumnPinning = debounce((pinning: ColumnPinningState) => {
    const excludeLeft = pinning.left?.filter((columnId) => !getIsDefaultColumn(columnId));
    const filteredData = { ...pinning, left: excludeLeft };
    emit('change-column-pinning', filteredData);
  }, 100);

  const updateColumnOrderState = (
    updater: ColumnOrderState | ((state: ColumnOrderState) => ColumnOrderState),
  ) => {
    columnOrderState.value =
      typeof updater === 'function' ? updater(columnOrderState.value) : updater;

    saveEnvColumnOrder(columnOrderState.value);

    emitColumnOrder(columnOrderState.value);
  };

  const updateColumnPinningState = (
    updater: ColumnPinningState | ((state: ColumnPinningState) => ColumnPinningState),
  ) => {
    const newPinning = typeof updater === 'function' ? updater(columnPinningState.value) : updater;

    if (isEqual(columnPinningState.value, newPinning)) {
      return;
    }

    columnPinningState.value = newPinning;
    emitColumnPinning(newPinning);
  };

  const columnOrderAndPinningOptions = computed<Partial<GridTableInstanceOptions<RowDataType>>>(
    () => ({
      onColumnOrderChange: updateColumnOrderState,
      onColumnPinningChange: updateColumnPinningState,
    }),
  );

  const initialColumnPinningState = computed<ColumnPinningState>(() => {
    const { leftPinningKeys, rightPinningKeys } = columnPinningInfo.value || {};
    const leftSet = new Set<string>();
    const rightSet = new Set<string>();

    if (useNumberColumn.value) {
      leftSet.add(NUMBER_COLUMN_ID);
    }
    if (rowSelectionInfo.value.useCheckbox) {
      leftSet.add(CHECKBOX_COLUMN_ID);
    }
    if (detailRowInfo.value.use) {
      leftSet.add(EXPAND_BTN_COLUMN_ID);
    }

    if (leftPinningKeys?.length) {
      leftPinningKeys.forEach((key) => leftSet.add(key));
    }
    if (rightPinningKeys?.length) {
      rightPinningKeys.forEach((key) => rightSet.add(key));
    }

    const left = Array.from(leftSet);
    const right = Array.from(rightSet);

    return {
      left,
      right,
    };
  });

  const setColumnOrderAndPinningState = (): void => {
    const envColumnOrder = getEnv(envKey.value)?.columnOrder as ColumnOrderState | undefined;
    const leftPinnedIds = initialColumnPinningState.value.left || [];
    const rightPinnedIds = initialColumnPinningState.value.right || [];
    const pinnedSet = new Set<string>([...leftPinnedIds, ...rightPinnedIds]);

    // 모든 계층의 컬럼 id를 평탄화해서 가져오는 함수
    const getAllColumnIds = (columns: GridColumnDef<RowDataType>[] = []): string[] => {
      const ids: string[] = [];

      columns.forEach((column) => {
        if (column.id) ids.push(column.id);
        if (column.columns?.length) {
          ids.push(...getAllColumnIds(column.columns));
        }
      });

      return ids;
    };

    const propsColumnIds = getAllColumnIds(props.columns);

    const mergedColumnIds = Array.from(
      new Set([...leftPinnedIds, ...propsColumnIds, ...rightPinnedIds]),
    );

    // env에 저장된 순서를 반영하면서 새로 추가된 중앙 컬럼을 뒤에 붙이는 헬퍼
    const buildCenterOrder = (
      envOrder: ColumnOrderState | undefined,
      availableIds: string[],
      pinned: Set<string>,
    ) => {
      const centerFromEnv = (envOrder ?? []).filter(
        (id) => availableIds.includes(id) && !pinned.has(id),
      );
      const remainingCenter = availableIds.filter(
        (id) => !pinned.has(id) && !centerFromEnv.includes(id),
      );

      return [...centerFromEnv, ...remainingCenter];
    };

    let columnOrder: ColumnOrderState;

    if (envColumnOrder?.length) {
      const centerOrder = buildCenterOrder(envColumnOrder, mergedColumnIds, pinnedSet);
      columnOrder = [...leftPinnedIds, ...centerOrder, ...rightPinnedIds];
    } else {
      const centerIds = buildCenterOrder(undefined, mergedColumnIds, pinnedSet);
      columnOrder = [...leftPinnedIds, ...centerIds, ...rightPinnedIds];
    }

    columnOrderState.value = columnOrder;
    columnPinningState.value = initialColumnPinningState.value;
    table.value?.setColumnOrder(columnOrder);
    table.value?.setColumnPinning(initialColumnPinningState.value);
  };

  onBeforeUnmount(() => {
    saveEnvColumnOrder.flush();
    emitColumnOrder.cancel();
    emitColumnPinning.cancel();
  });

  return {
    columnOrderState,
    columnOrderingInfo,
    columnPinningInfo,
    columnPinningState,
    leftColumnIds,
    rightColumnIds,
    columnOrderAndPinningOptions,
    setColumnOrderAndPinningState,
  };
};
